import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Paper,
  Divider,
  Chip,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
} from '@mui/icons-material'
import { participantViewService, type Question, type QuestionnaireData, type QuestionnaireAnswer } from '@/services/participantViewService'
import { getQuestionTypeLabel } from '@/utils/questionTypeLabel'
import { resolveApiErrorMessage } from '@/utils/apiErrorMessage'

export default function FillQuestionnaire() {
  const { t } = useTranslation('questionnaires')
  const { t: tCommon } = useTranslation('common')
  const { t: tErrors } = useTranslation('errors')
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const [questionnaire, setQuestionnaire] = useState<QuestionnaireData | null>(null)
  const [answers, setAnswers] = useState<Record<number, QuestionnaireAnswer>>({})
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    if (projectId) {
      loadQuestionnaire()
    }
  }, [projectId])

  const loadQuestionnaire = async () => {
    if (!projectId) return
    
    try {
      setLoading(true)
      setError(null)
      const questionnaireData = await participantViewService.getProjectQuestionnaire(Number(projectId))
      setQuestionnaire(questionnaireData)
      
      const initialAnswers: Record<number, QuestionnaireAnswer> = {}
      questionnaireData.questions.forEach(q => {
        initialAnswers[q.id] = {
          question_id: q.id,
          note: '',
        }
      })
      setAnswers(initialAnswers)
    } catch (err) {
      setError(resolveApiErrorMessage(err, tErrors) || t('loadQuestionnaireFailed'))
    } finally {
      setLoading(false)
    }
  }

  const handleAnswerChange = (questionId: number, value: any, field: keyof QuestionnaireAnswer = 'choices') => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [field]: field === 'choices' ? (Array.isArray(value) ? value : [value]) : value,
      }
    }))
  }

  const handleNoteChange = (questionId: number, note: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        note,
      }
    }))
  }

  const renderQuestion = (question: Question) => {
    const answer = answers[question.id] || { question_id: question.id }

    if (question.question_type === 'TEXT') {
      return (
        <TextField
          key={question.id}
          fullWidth
          multiline
          rows={3}
          label={t('yourAnswer')}
          value={Array.isArray(answer.choices) ? answer.choices[0] || '' : ''}
          onChange={(e) => handleAnswerChange(question.id, e.target.value)}
          sx={{ mt: 1 }}
        />
      )
    }

    if (question.question_type === 'SINGLE_CHOICE') {
      return (
        <FormControl key={question.id} component="fieldset" sx={{ mt: 1 }}>
          <RadioGroup
            value={answer.choices?.[0] ?? ''}
            onChange={(e) => handleAnswerChange(question.id, Number(e.target.value))}
          >
            {question.choices.map((choice) => (
              <FormControlLabel
                key={choice.value}
                value={choice.value}
                control={<Radio />}
                label={choice.text}
              />
            ))}
          </RadioGroup>
        </FormControl>
      )
    }

    if (question.question_type === 'MULTIPLE_CHOICE') {
      return (
        <FormControl key={question.id} component="fieldset" sx={{ mt: 1 }}>
          <FormGroup>
            {question.choices.map((choice) => (
              <FormControlLabel
                key={choice.value}
                control={
                  <Checkbox
                    checked={answer.choices?.includes(choice.value) || false}
                    onChange={(e) => {
                      const currentChoices = answer.choices || []
                      const newChoices = e.target.checked
                        ? [...currentChoices, choice.value]
                        : currentChoices.filter((c: number) => c !== choice.value)
                      handleAnswerChange(question.id, newChoices)
                    }}
                  />
                }
                label={choice.text}
              />
            ))}
          </FormGroup>
        </FormControl>
      )
    }

    return null
  }

  const handleSubmit = async () => {
    if (!projectId) return

    try {
      setSubmitting(true)
      setError(null)
      setSuccess(null)

      const answersArray = Object.values(answers)
      const response = await participantViewService.submitQuestionnaire(Number(projectId), answersArray)

      setSuccess(response.message)
      
      setTimeout(() => {
        navigate('/participant/dashboard')
      }, 2000)
    } catch (err) {
      setError(resolveApiErrorMessage(err, tErrors) || t('submitFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  const isQuestionnaireValid = () => {
    if (!questionnaire) return false
    
    return questionnaire.questions.every(q => {
      const answer = answers[q.id]
      if (q.question_type === 'TEXT') {
        const val = answer?.choices?.[0]
        return (typeof val === 'string' ? val : String(val ?? '')).trim().length > 0
      }
      return (answer?.choices?.length ?? 0) > 0
    })
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error && !questionnaire) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/participant/dashboard')} sx={{ mb: 2 }}>
          {t('backToDashboard')}
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  if (!questionnaire) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/participant/dashboard')} sx={{ mb: 2 }}>
          {t('backToDashboard')}
        </Button>
        <Alert severity="info">{t('noQuestionnaireFound')}</Alert>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/participant/dashboard')}>
          {t('backToDashboard')}
        </Button>
      </Box>

      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {questionnaire.template_name}
        </Typography>
        {questionnaire.instructions && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2" component="div">
              {questionnaire.instructions}
            </Typography>
          </Alert>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        {questionnaire.questions.map((question, index) => (
          <React.Fragment key={question.id}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip label={t('questionNumber', { number: index + 1 })} size="small" color="primary" />
                {question.section_name && (
                  <Chip label={question.section_name} size="small" variant="outlined" />
                )}
                <Chip label={getQuestionTypeLabel(question.question_type, t)} size="small" variant="outlined" />
              </Box>
              <Typography variant="h6" gutterBottom>
                {question.text}
              </Typography>
              {renderQuestion(question)}
              <TextField
                fullWidth
                label={t('additionalNotes')}
                multiline
                rows={2}
                value={answers[question.id]?.note || ''}
                onChange={(e) => handleNoteChange(question.id, e.target.value)}
                sx={{ mt: 2 }}
              />
            </Box>
            {index < questionnaire.questions.length - 1 && <Divider sx={{ my: 3 }} />}
          </React.Fragment>
        ))}
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/participant/dashboard')}
          disabled={submitting}
        >
          {tCommon('cancel')}
        </Button>
        <Button
          variant="contained"
          startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
          onClick={handleSubmit}
          disabled={!isQuestionnaireValid() || submitting}
        >
          {t('submitQuestionnaire')}
        </Button>
      </Box>
    </Box>
  )
}

